// src/pages/lms/LMSTeachers.jsx — V3 FINAL
// ROOT CAUSE FIXES:
// 1. TEACHER NOT SAVING: removed institution_account_id + is_class_teacher
//    (columns don't exist in table). Only sends columns that exist.
// 2. TYPING LAG: Form modal is now a SEPARATE isolated component TeacherFormModal
//    with its own local state + uncontrolled refs for text inputs.
//    Parent component never re-renders when user types.
// 3. Error is now shown from supabase response correctly.

import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { Plus, X, Search, Trash2, User, CheckCircle } from 'lucide-react';

const DESIGNATIONS = ['lecturer','assistant_professor','associate_professor','professor','hod','dean','principal','director','other'];
const EMPLOYMENT   = ['permanent','visiting','contract','adjunct'];
const STATUSES     = ['active','on_leave','suspended','retired','resigned'];
const DEPARTMENTS  = [
  'Computer Science','Business Administration','Engineering','Mathematics',
  'Physics','Chemistry','Biology','English','Urdu','Islamic Studies',
  'Social Sciences','Economics','Law','Medical','Architecture','Arts','Other',
];
const DES_LABELS = {
  lecturer:'Lecturer', assistant_professor:'Asst. Professor',
  associate_professor:'Assoc. Professor', professor:'Professor',
  hod:'Head of Department', dean:'Dean', principal:'Principal',
  director:'Director', other:'Other',
};
const INP = {
  width:'100%', padding:'9px 12px', borderRadius:8,
  border:'1.5px solid #E5E7EB', fontSize:13,
  boxSizing:'border-box', outline:'none', color:'#111827',
};

// ─────────────────────────────────────────────────────
// TEACHER FORM MODAL — completely isolated
// Has its own state. Parent never re-renders on keypress.
// ─────────────────────────────────────────────────────
function TeacherFormModal({ editTeacher, institutionId, onSave, onClose }) {
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [uploading,setUploading]= useState(false);
  const [photoUrl, setPhotoUrl] = useState(editTeacher?.profile_photo_url||'');
  const [subjects, setSubjects] = useState(editTeacher?.subjects||[]);
  // Selects — controlled (selects don't have lag, only text inputs do)
  const [designation,    setDesignation]    = useState(editTeacher?.designation||'lecturer');
  const [department,     setDepartment]     = useState(editTeacher?.department||'');
  const [gender,         setGender]         = useState(editTeacher?.gender||'male');
  const [employmentType, setEmploymentType] = useState(editTeacher?.employment_type||'permanent');
  const [status,         setStatus]         = useState(editTeacher?.status||'active');
  // Text inputs — UNCONTROLLED refs (zero re-renders = zero lag)
  const nameRef    = useRef(null);
  const emailRef   = useRef(null);
  const phoneRef   = useRef(null);
  const cnicRef    = useRef(null);
  const qualRef    = useRef(null);
  const specRef    = useRef(null);
  const expRef     = useRef(null);
  const empIdRef   = useRef(null);
  const joinRef    = useRef(null);
  const bioRef     = useRef(null);
  const subjRef    = useRef(null);

  const addSubject = () => {
    const val = subjRef.current?.value?.trim()||'';
    if (!val) return;
    setSubjects(s => [...s, val]);
    if (subjRef.current) subjRef.current.value = '';
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const path = `${institutionId}/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.storage
        .from('lms-teacher-photos').upload(path, file, { upsert:true });
      if (upErr) throw upErr;
      const { data:{ publicUrl } } = supabase.storage
        .from('lms-teacher-photos').getPublicUrl(path);
      setPhotoUrl(publicUrl);
    } catch(e) { setError('Photo upload failed: '+e.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const fullName = nameRef.current?.value?.trim()||'';
    if (!fullName) { setError('Full name is required'); return; }
    setSaving(true); setError('');
    try {
      // ONLY send columns that exist in lms_teachers table
      const data = {
        institution_id:   institutionId,
        full_name:        fullName,
        email:            emailRef.current?.value?.trim()||null,
        phone:            phoneRef.current?.value?.trim()||null,
        cnic:             cnicRef.current?.value?.trim()||null,
        gender,
        designation,
        department:       department||null,
        qualification:    qualRef.current?.value?.trim()||null,
        specialization:   specRef.current?.value?.trim()||null,
        experience_years: Number(expRef.current?.value)||0,
        employment_type:  employmentType,
        employee_id:      empIdRef.current?.value?.trim()||null,
        joining_date:     joinRef.current?.value||null,
        subjects:         subjects,
        status,
        bio:              bioRef.current?.value?.trim()||null,
        profile_photo_url:photoUrl||null,
        // teacher_username and teacher_pin left null — set in Attendance Settings
      };

      let err;
      if (editTeacher) {
        const res = await supabase.from('lms_teachers').update(data).eq('id', editTeacher.id);
        err = res.error;
      } else {
        const res = await supabase.from('lms_teachers').insert(data);
        err = res.error;
      }

      if (err) {
        console.error('Teacher save error:', err);
        throw err;
      }
      onSave(editTeacher ? 'Teacher updated!' : 'Teacher added!');
    } catch(e) {
      setError(e.message || 'Save failed. Check browser console (F12).');
      console.error('Save teacher full error:', e);
    } finally { setSaving(false); }
  };

  const F = ({label,children,col}) => (
    <div style={{gridColumn:col||'span 1'}}>
      <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>{label}</label>
      {children}
    </div>
  );

  const initial = (editTeacher?.full_name||'?')[0]?.toUpperCase()||'?';

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:680,maxHeight:'92vh',overflowY:'auto'}}>
        {/* Header */}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,color:'#111827',fontSize:18}}>
            {editTeacher ? 'Edit Teacher' : 'Add New Teacher'}
          </h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>

        <div style={{padding:24}}>
          {error && (
            <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B',fontWeight:600}}>
              ❌ {error}
            </div>
          )}

          {/* Photo */}
          <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:20,padding:16,background:'#F9FAFB',borderRadius:12}}>
            <div style={{width:72,height:72,borderRadius:'50%',overflow:'hidden',flexShrink:0,border:'2px solid #E5E7EB'}}>
              {photoUrl ? (
                <img src={photoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              ) : (
                <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:24,fontWeight:800}}>
                  {initial}
                </div>
              )}
            </div>
            <div>
              <div style={{fontWeight:700,color:'#111827',fontSize:13,marginBottom:4}}>Profile Photo</div>
              <label style={{cursor:'pointer'}}>
                <input type="file" accept="image/*" style={{display:'none'}}
                  onChange={e=>e.target.files?.[0]&&uploadPhoto(e.target.files[0])}/>
                <span style={{fontSize:12,color:'#4F8EF7',fontWeight:600,padding:'6px 12px',border:'1px solid #BFDBFE',borderRadius:6,background:'#EFF6FF'}}>
                  {uploading?'Uploading...':'📷 Upload Photo'}
                </span>
              </label>
            </div>
          </div>

          {/* Form */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <F label="Full Name *" col="span 2">
              <input ref={nameRef} defaultValue={editTeacher?.full_name||''}
                placeholder="e.g. Dr. Ahmed Ali" style={INP}/>
            </F>
            <F label="Email">
              <input ref={emailRef} type="email" defaultValue={editTeacher?.email||''}
                placeholder="teacher@university.edu" style={INP}/>
            </F>
            <F label="Phone">
              <input ref={phoneRef} defaultValue={editTeacher?.phone||''}
                placeholder="+92 300 1234567" style={INP}/>
            </F>
            <F label="Designation *">
              <select value={designation} onChange={e=>setDesignation(e.target.value)} style={INP}>
                {DESIGNATIONS.map(d=><option key={d} value={d}>{DES_LABELS[d]||d}</option>)}
              </select>
            </F>
            <F label="Department">
              <select value={department} onChange={e=>setDepartment(e.target.value)} style={INP}>
                <option value="">Select Department</option>
                {DEPARTMENTS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </F>
            <F label="Qualification">
              <input ref={qualRef} defaultValue={editTeacher?.qualification||''}
                placeholder="e.g. PhD Computer Science" style={INP}/>
            </F>
            <F label="Specialization">
              <input ref={specRef} defaultValue={editTeacher?.specialization||''}
                placeholder="e.g. Machine Learning" style={INP}/>
            </F>
            <F label="Experience (years)">
              <input ref={expRef} type="number" min="0"
                defaultValue={editTeacher?.experience_years||0} style={INP}/>
            </F>
            <F label="Employment Type">
              <select value={employmentType} onChange={e=>setEmploymentType(e.target.value)} style={INP}>
                {EMPLOYMENT.map(e=><option key={e} value={e}>{e.charAt(0).toUpperCase()+e.slice(1)}</option>)}
              </select>
            </F>
            <F label="Gender">
              <select value={gender} onChange={e=>setGender(e.target.value)} style={INP}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </F>
            <F label="Employee ID">
              <input ref={empIdRef} defaultValue={editTeacher?.employee_id||''}
                placeholder="e.g. EMP-001" style={INP}/>
            </F>
            <F label="Joining Date">
              <input ref={joinRef} type="date"
                defaultValue={editTeacher?.joining_date||''} style={INP}/>
            </F>
            <F label="CNIC">
              <input ref={cnicRef} defaultValue={editTeacher?.cnic||''}
                placeholder="35202-1234567-1" style={INP}/>
            </F>
            <F label="Status">
              <select value={status} onChange={e=>setStatus(e.target.value)} style={INP}>
                {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </select>
            </F>

            {/* Subjects */}
            <F label="Subjects Taught" col="span 2">
              <div style={{display:'flex',gap:8,marginBottom:8}}>
                <input ref={subjRef} defaultValue=""
                  onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addSubject(); }}}
                  placeholder="Type subject and press Enter or click Add"
                  style={{...INP,flex:1}}/>
                <button onClick={addSubject} type="button"
                  style={{padding:'9px 16px',borderRadius:8,border:'none',background:'#4F8EF7',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
                  + Add
                </button>
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {subjects.map((s,i)=>(
                  <span key={i} style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12,fontWeight:600,color:'#4F8EF7',background:'#EFF6FF',padding:'4px 10px',borderRadius:6,border:'1px solid #BFDBFE'}}>
                    {s}
                    <button onClick={()=>setSubjects(sub=>sub.filter((_,j)=>j!==i))}
                      style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center'}}>
                      <X size={11} color="#4F8EF7"/>
                    </button>
                  </span>
                ))}
              </div>
            </F>

            {/* Bio */}
            <F label="Bio / Notes" col="span 2">
              <textarea ref={bioRef} defaultValue={editTeacher?.bio||''}
                placeholder="Optional bio or notes..." rows={2}
                style={{...INP,resize:'vertical'}}/>
            </F>
          </div>

          {/* Attendance PIN note */}
          <div style={{marginTop:14,background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'10px 14px',fontSize:12,color:'#065F46'}}>
            ℹ️ To allow this teacher to mark attendance, go to <strong>Attendance → Settings → Teacher PINs</strong> and set their username + PIN after saving.
          </div>

          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={onClose}
              style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer',fontSize:13}}>
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
              {saving ? 'Saving...' : editTeacher ? 'Update Teacher' : 'Add Teacher'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// MAIN COMPONENT — no form state here, no re-render on type
// ─────────────────────────────────────────────────────
export default function LMSTeachers() {
  const { institution } = useLMSAuth();
  const [teachers,  setTeachers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [deptF,     setDeptF]     = useState('');
  const [statusF,   setStatusF]   = useState('active');
  const [showForm,  setShowForm]  = useState(false);
  const [editTeacher,setEditTeacher]= useState(null);
  const [success,   setSuccess]   = useState('');
  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const timer = useRef(null);
  const handleSearch = v => {
    setSearchInput(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(()=>setSearch(v), 200);
  };

  useEffect(()=>{ if(institution?.id) load(); },[institution?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('lms_teachers')
        .select('*').eq('institution_id',institution.id).order('full_name');
      if (error) { console.error('Load teachers error:',error); }
      setTeachers(data||[]);
    } catch(e){ console.error(e); }
    finally{ setLoading(false); }
  };

  const handleSave = async (msg) => {
    setSuccess(msg);
    setShowForm(false);
    setEditTeacher(null);
    await load();
    setTimeout(()=>setSuccess(''),4000);
  };

  const deleteTeacher = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    await supabase.from('lms_classes')
      .update({class_teacher_id:null,class_teacher_name:''})
      .eq('class_teacher_id',id).eq('institution_id',institution.id);
    const { error } = await supabase.from('lms_teachers').delete().eq('id',id);
    if (error) { console.error('Delete error:',error); alert('Delete failed: '+error.message); return; }
    await load();
  };

  const filtered = useMemo(()=>{
    let f = teachers;
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(t=>
        (t.full_name||'').toLowerCase().includes(q)||
        (t.email||'').toLowerCase().includes(q)||
        (t.department||'').toLowerCase().includes(q)||
        (t.subjects||[]).some(s=>s.toLowerCase().includes(q))||
        (t.employee_id||'').toLowerCase().includes(q)
      );
    }
    if (deptF)   f = f.filter(t=>t.department===deptF);
    if (statusF) f = f.filter(t=>t.status===statusF);
    return f;
  },[teachers,search,deptF,statusF]);

  const depts = useMemo(()=>[...new Set(teachers.map(t=>t.department).filter(Boolean))],[teachers]);

  const statusColor = s=>({
    active:'#10B981',on_leave:'#F59E0B',
    suspended:'#EF4444',retired:'#94A3B8',resigned:'#6B7280',
  }[s]||'#94A3B8');

  const activeCount   = teachers.filter(t=>t.status==='active').length;
  const visitingCount = teachers.filter(t=>t.employment_type==='visiting').length;
  const leaveCount    = teachers.filter(t=>t.status==='on_leave').length;

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="teachers">

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Teachers</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>
              {filtered.length} of {teachers.length} teachers
            </p>
          </div>
          <button onClick={()=>{setShowForm(true);setEditTeacher(null);}}
            style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
            <Plus size={16}/> Add Teacher
          </button>
        </div>

        {success && (
          <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
            <CheckCircle size={15} color="#10B981"/> {success}
          </div>
        )}

        {/* Info */}
        <div style={{background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)',border:'1px solid #BFDBFE',borderRadius:14,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#1D4ED8'}}>
          <strong>👨‍🏫 Class Teacher:</strong> After adding a teacher, go to <strong>Classes → Edit Class</strong> to assign them as class teacher. Then set their attendance PIN in <strong>Attendance → Settings</strong>.
        </div>

        {/* Stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:12,marginBottom:20}}>
          {[
            {l:'Total',   v:teachers.length, c:'#4F8EF7', e:'👨‍🏫'},
            {l:'Active',  v:activeCount,     c:'#10B981', e:'✅'},
            {l:'Visiting',v:visitingCount,   c:'#F59E0B', e:'🔄'},
            {l:'On Leave',v:leaveCount,      c:'#EF4444', e:'🏖'},
          ].map(s=>(
            <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'14px 16px'}}>
              <div style={{fontSize:20,marginBottom:4}}>{s.e}</div>
              <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
              <div style={{fontSize:11,color:'#6B7280'}}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:14,marginBottom:20,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',flex:1,minWidth:200}}>
            <Search size={15} color="#9CA3AF" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
            <input value={searchInput} onChange={e=>handleSearch(e.target.value)}
              placeholder="Search name, email, department, subject..."
              style={{...INP,paddingLeft:32}}/>
          </div>
          <select value={deptF} onChange={e=>setDeptF(e.target.value)} style={{...INP,width:'auto'}}>
            <option value="">All Departments</option>
            {depts.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          <select value={statusF} onChange={e=>setStatusF(e.target.value)} style={{...INP,width:'auto'}}>
            <option value="">All Status</option>
            {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select>
          {(search||deptF||statusF) && (
            <button onClick={()=>{setSearchInput('');setSearch('');setDeptF('');setStatusF('');}}
              style={{padding:'8px 12px',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer',fontSize:12,color:'#374151',display:'flex',alignItems:'center',gap:5}}>
              <X size={13}/> Clear
            </button>
          )}
        </div>

        {/* Teachers grid */}
        {loading ? (
          <div style={{textAlign:'center',padding:48,color:'#9CA3AF'}}>Loading teachers...</div>
        ) : filtered.length===0 ? (
          <div style={{textAlign:'center',padding:60,background:'#fff',borderRadius:16,border:'1px solid #E5E7EB'}}>
            <User size={48} color="#E5E7EB" style={{marginBottom:12}}/>
            <div style={{fontSize:15,fontWeight:700,color:'#374151',marginBottom:6}}>
              {teachers.length===0 ? 'No teachers yet' : 'No teachers match filter'}
            </div>
            <div style={{fontSize:13,color:'#9CA3AF'}}>
              {teachers.length===0 ? 'Click "Add Teacher" to add your first teacher' : 'Try clearing filters'}
            </div>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16}}>
            {filtered.map(t=>(
              <div key={t.id} style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'hidden'}}>
                {/* Card header */}
                <div style={{background:'linear-gradient(135deg,#4F8EF710,#7C3AED08)',padding:'16px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'flex-start',gap:12}}>
                  <div style={{width:52,height:52,borderRadius:'50%',flexShrink:0,overflow:'hidden',border:'2px solid #4F8EF740'}}>
                    {t.profile_photo_url ? (
                      <img src={t.profile_photo_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    ) : (
                      <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:20,fontWeight:800}}>
                        {t.full_name[0]}
                      </div>
                    )}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:800,color:'#111827',fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.full_name}</div>
                    <div style={{fontSize:12,color:'#7C3AED',fontWeight:600}}>{DES_LABELS[t.designation]||t.designation}</div>
                    {t.department && <div style={{fontSize:11,color:'#6B7280'}}>{t.department}</div>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                    <span style={{fontSize:10,fontWeight:700,color:statusColor(t.status),background:`${statusColor(t.status)}15`,padding:'2px 8px',borderRadius:4,textTransform:'uppercase'}}>
                      {t.status.replace('_',' ')}
                    </span>
                    <span style={{fontSize:10,color:'#94A3B8'}}>{t.employment_type}</span>
                  </div>
                </div>
                {/* Card body */}
                <div style={{padding:'12px 18px'}}>
                  {t.email           && <div style={{fontSize:12,color:'#6B7280',marginBottom:3}}>📧 {t.email}</div>}
                  {t.phone           && <div style={{fontSize:12,color:'#6B7280',marginBottom:3}}>📞 {t.phone}</div>}
                  {t.qualification   && <div style={{fontSize:12,color:'#6B7280',marginBottom:3}}>🎓 {t.qualification}</div>}
                  {t.experience_years>0 && <div style={{fontSize:12,color:'#6B7280',marginBottom:6}}>⏱ {t.experience_years} yrs experience</div>}
                  {t.subjects?.length>0 && (
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
                      {t.subjects.map(s=>(
                        <span key={s} style={{fontSize:10,fontWeight:600,color:'#4F8EF7',background:'#EFF6FF',padding:'2px 8px',borderRadius:4}}>{s}</span>
                      ))}
                    </div>
                  )}
                  {t.teacher_pin && (
                    <div style={{fontSize:11,color:'#10B981',background:'#F0FDF4',padding:'4px 8px',borderRadius:5,marginBottom:6}}>
                      ✅ Attendance PIN set · @{t.teacher_username||'no username'}
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,marginTop:8}}>
                    <button onClick={()=>{setEditTeacher(t);setShowForm(true);}}
                      style={{flex:1,padding:'7px 0',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer',fontSize:12,fontWeight:600,color:'#374151'}}>
                      ✏️ Edit
                    </button>
                    <button onClick={()=>deleteTeacher(t.id,t.full_name)}
                      style={{padding:'7px 12px',borderRadius:8,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}>
                      <Trash2 size={13} color="#EF4444"/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal — rendered here, isolated from grid */}
        {showForm && (
          <TeacherFormModal
            key={editTeacher?.id||'new-teacher'}
            editTeacher={editTeacher}
            institutionId={institution?.id}
            onSave={handleSave}
            onClose={()=>{setShowForm(false);setEditTeacher(null);}}
          />
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}